// src/env.ts
var ENV = typeof process !== "undefined" && process.env ? process.env : {};
function envGet(key) {
  return ENV[key] ?? ENV[`VITE_${key}`];
}

// src/observers.ts
var observers = /* @__PURE__ */ new Set();
function onLogConfigChange(fn) {
  observers.add(fn);
  return () => {
    observers.delete(fn);
  };
}
function notifyLogConfigChange() {
  for (const fn of observers) {
    try {
      fn();
    } catch {
    }
  }
}

// src/runtime-override.ts
function getRuntimeNamespaceFilter() {
  const g = globalThis;
  return g.__ALMADAR_DEBUG__;
}
function setRuntimeNamespaceFilter(value) {
  const g = globalThis;
  if (value === void 0) {
    delete g.__ALMADAR_DEBUG__;
  } else {
    g.__ALMADAR_DEBUG__ = value;
  }
  notifyLogConfigChange();
}

// src/logger.ts
var LEVEL_PRIORITY = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };
var NODE_ENV = envGet("NODE_ENV") ?? "development";
var INITIAL_LEVEL = (envGet("LOG_LEVEL") ?? (NODE_ENV === "production" ? "warn" : "debug")).toUpperCase();
var currentLevel = LEVEL_PRIORITY[INITIAL_LEVEL] !== void 0 ? INITIAL_LEVEL : "DEBUG";
var namespaceLevels = /* @__PURE__ */ new Map();
function getLogLevel() {
  return currentLevel;
}
function setLogLevel(level) {
  if (LEVEL_PRIORITY[level] === void 0) return;
  currentLevel = level;
  notifyLogConfigChange();
}
function setNamespaceLevel(namespace, level) {
  if (level === null) {
    namespaceLevels.delete(namespace);
  } else if (LEVEL_PRIORITY[level] !== void 0) {
    namespaceLevels.set(namespace, level);
  }
  notifyLogConfigChange();
}
function getNamespaceLevel(namespace) {
  return namespaceLevels.get(namespace);
}
function getNamespaceLevels() {
  return namespaceLevels;
}
var ENV_FILTER_RAW = envGet("ALMADAR_DEBUG") ?? "";
var ENV_FILTER = ENV_FILTER_RAW.split(",").map((s) => s.trim()).filter(Boolean);
function matchesPatterns(namespace, patterns) {
  if (patterns.length === 0) return false;
  for (const pattern of patterns) {
    if (pattern === "*" || pattern === "almadar:*") return true;
    if (pattern.endsWith(":*")) {
      if (namespace.startsWith(pattern.slice(0, -1))) return true;
      continue;
    }
    if (namespace === pattern) return true;
  }
  return false;
}
function namespaceAllowed(namespace) {
  const runtime = getRuntimeNamespaceFilter();
  if (runtime !== void 0) {
    if (runtime === "") return false;
    const patterns = runtime.split(",").map((s) => s.trim()).filter(Boolean);
    return matchesPatterns(namespace, patterns);
  }
  if (ENV_FILTER.length === 0) return true;
  return matchesPatterns(namespace, ENV_FILTER);
}
function effectiveMinPriority(namespace) {
  const exact = namespaceLevels.get(namespace);
  if (exact !== void 0) return LEVEL_PRIORITY[exact];
  for (const [pattern, level] of namespaceLevels) {
    if (pattern.endsWith(":*") && namespace.startsWith(pattern.slice(0, -1))) {
      return LEVEL_PRIORITY[level];
    }
  }
  return LEVEL_PRIORITY[currentLevel];
}
function isLogLevelEnabled(level, namespace) {
  if (LEVEL_PRIORITY[level] < effectiveMinPriority(namespace)) return false;
  if (level === "DEBUG" || level === "INFO") {
    return namespaceAllowed(namespace);
  }
  return true;
}
function resolveData(data) {
  if (data === void 0) return void 0;
  if (typeof data === "function") return data();
  return data;
}
function attachCorrelation(data, cid) {
  if (!cid) return data;
  return { ...data ?? {}, cid };
}
function emit(level, prefix, message, payload) {
  const data = payload ?? "";
  switch (level) {
    case "DEBUG":
      console.debug(prefix, message, data);
      break;
    case "INFO":
      console.info(prefix, message, data);
      break;
    case "WARN":
      console.warn(prefix, message, data);
      break;
    case "ERROR":
      console.error(prefix, message, data);
      break;
  }
}
function createLogger(namespace) {
  const prefix = `[${namespace}]`;
  const dispatch = (level, message, data, cid) => {
    if (LEVEL_PRIORITY[level] < effectiveMinPriority(namespace)) return;
    if ((level === "DEBUG" || level === "INFO") && !namespaceAllowed(namespace)) return;
    const resolved = resolveData(data);
    emit(level, prefix, message, attachCorrelation(resolved, cid));
  };
  return {
    debug: (msg, data, cid) => dispatch("DEBUG", msg, data, cid),
    info: (msg, data, cid) => dispatch("INFO", msg, data, cid),
    warn: (msg, data, cid) => dispatch("WARN", msg, data, cid),
    error: (msg, data, cid) => dispatch("ERROR", msg, data, cid)
  };
}
var _cidCounter = 0;
function generateCorrelationId() {
  return `evt-${Date.now()}-${++_cidCounter}`;
}

// src/persistence.ts
var DEFAULT_KEY = "almadar:logger:prefs";
var persistenceKey = null;
var unsubscribe = null;
function getStorage() {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
  }
  return null;
}
function write() {
  if (persistenceKey === null) return;
  const storage = getStorage();
  if (storage === null) return;
  const prefs = {
    namespaceFilter: getRuntimeNamespaceFilter(),
    level: getLogLevel(),
    namespaceLevels: Object.fromEntries(getNamespaceLevels())
  };
  try {
    storage.setItem(persistenceKey, JSON.stringify(prefs));
  } catch {
  }
}
function read() {
  if (persistenceKey === null) return null;
  const storage = getStorage();
  if (storage === null) return null;
  try {
    const raw = storage.getItem(persistenceKey);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function enableLogPersistence(key = DEFAULT_KEY) {
  persistenceKey = key;
  const stored = read();
  if (stored !== null) {
    if (stored.namespaceFilter !== void 0) {
      setRuntimeNamespaceFilter(stored.namespaceFilter);
    }
    if (stored.level !== void 0) {
      setLogLevel(stored.level);
    }
    if (stored.namespaceLevels !== void 0) {
      for (const [ns, lvl] of Object.entries(stored.namespaceLevels)) {
        setNamespaceLevel(ns, lvl);
      }
    }
  }
  if (unsubscribe !== null) unsubscribe();
  unsubscribe = onLogConfigChange(write);
}
function disableLogPersistence() {
  if (unsubscribe !== null) {
    unsubscribe();
    unsubscribe = null;
  }
  persistenceKey = null;
}
function clearLogPersistence(key = DEFAULT_KEY) {
  const storage = getStorage();
  if (storage !== null) {
    try {
      storage.removeItem(key);
    } catch {
    }
  }
  disableLogPersistence();
}

export { clearLogPersistence, createLogger, disableLogPersistence, enableLogPersistence, generateCorrelationId, getLogLevel, getNamespaceLevel, getNamespaceLevels, getRuntimeNamespaceFilter, isLogLevelEnabled, onLogConfigChange, setLogLevel, setNamespaceLevel, setRuntimeNamespaceFilter };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map